import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { PortalShell, QuoteBreakdownPanel, StatusBadge } from "@/components/business/B2BPortal";
import { SEOHead } from "@/components/shared/SEOHead";
import { productByIdFromSnapshot, quoteForProjectFromSnapshot, useBusinessPortalSnapshot } from "@/lib/b2bPortalApi";
import type { PlacementSpec } from "@/lib/b2bPortalData";

export const Route = createFileRoute("/business/portal/projects/$projectId")({
  component: ProjectDetailPage,
});

function ProjectDetailPage() {
  const { projectId } = Route.useParams();
  const portal = useBusinessPortalSnapshot();
  const { account, brandAssets, projects } = portal.data;
  const project = projects.find((item) => item.id === projectId);

  if (portal.loading) {
    return (
      <PortalShell active="/business/portal/projects" title="Loading project" description="Fetching your proof and quote." account={account}>
        <section className="b2b-card b2b-loading-card"><div /><div /><div /></section>
      </PortalShell>
    );
  }

  if (!project) {
    return (
      <PortalShell active="/business/portal/projects" title="Project not found" description="That project is not available in this workspace." account={account}>
        <Link to="/business/portal/projects" className="b2b-primary-button">Back to projects</Link>
      </PortalShell>
    );
  }

  const product = productByIdFromSnapshot(portal.data, project.productId);
  const variant = product?.variants.find((item) => item.id === project.variantId);
  const zone = product?.printZones.find((item) => item.id === project.placement.printZoneId);
  const quote = quoteForProjectFromSnapshot(portal.data, project.id);
  const [placement, setPlacement] = useState<PlacementSpec>(project.placement);
  const [selectedAssetId, setSelectedAssetId] = useState(project.brandAssetId);
  const [quoteRequested, setQuoteRequested] = useState(false);
  const safeAreaValid = useMemo(() => {
    const inset = zone?.safeInset ?? 0;
    const left = placement.x - placement.width / 2;
    const right = placement.x + placement.width / 2;
    const top = placement.y - placement.height / 2;
    const bottom = placement.y + placement.height / 2;
    return left >= inset && right <= 1 - inset && top >= inset && bottom <= 1 - inset;
  }, [placement, zone?.safeInset]);
  const selectedAsset = brandAssets.find((asset) => asset.id === selectedAssetId);
  const livePlacement = { ...placement, brandAssetId: selectedAssetId, safeAreaValid };

  return (
    <PortalShell
      active="/business/portal/projects"
      eyebrow="Proof"
      title={project.name}
      description="Review artwork placement and request a quote."
      account={account}
      actions={<button type="button" className="b2b-secondary-button" onClick={() => void portal.refresh()}>Refresh</button>}
    >
      <SEOHead title={project.name} description="Packaging proof and quote." path={`/business/portal/projects/${project.id}`} robots="noindex,follow" />

      <section className="b2b-project-detail">
        <div className="b2b-card b2b-mockup-card">
          <div className="b2b-card-row">
            <div>
              <span className="b2b-kicker">{product?.category ?? "Packaging"}</span>
              <h2>{product?.title ?? "Packaging proof"}</h2>
            </div>
            <StatusBadge status={project.status} tone={project.status === "approved" ? "good" : "warn"} />
          </div>
          <LiveMockup productStyle={product?.mockupStyle} assetName={selectedAsset?.name} placement={livePlacement} />
          <PlacementControls placement={placement} setPlacement={setPlacement} safeAreaValid={safeAreaValid} />
          <div className="b2b-action-row">
            <button className="b2b-secondary-button" type="button" onClick={() => window.print()}>Print proof</button>
            <button
              className="b2b-primary-button"
              type="button"
              disabled={!safeAreaValid || quoteRequested}
              onClick={() => setQuoteRequested(true)}
            >
              {quoteRequested ? "Quote requested" : "Request quote"}
            </button>
          </div>
        </div>

        <aside className="b2b-card b2b-spec-panel">
          <span className="b2b-kicker">Placement</span>
          <h3>{zone?.name ?? "Print area"}</h3>
          <dl>
            <div><dt>Option</dt><dd>{variant?.size ?? "Selected option"}</dd></div>
            <div><dt>Position</dt><dd>{Math.round(livePlacement.x * 100)}% / {Math.round(livePlacement.y * 100)}%</dd></div>
            <div><dt>Size</dt><dd>{Math.round(livePlacement.width * 100)}% / {Math.round(livePlacement.height * 100)}%</dd></div>
            <div><dt>Rotation</dt><dd>{livePlacement.rotationDegrees}°</dd></div>
            <div><dt>Safe area</dt><dd>{safeAreaValid ? "Ready" : "Needs adjustment"}</dd></div>
          </dl>
        </aside>
      </section>

      <section className="b2b-card">
        <div className="b2b-section-heading">
          <div>
            <span className="b2b-kicker">Artwork</span>
            <h2>Choose artwork</h2>
          </div>
          <Link to="/business/portal/brands" className="b2b-secondary-button">Create artwork</Link>
        </div>
        {brandAssets.length > 0 ? (
          <div className="b2b-artwork-picker-grid">
            {brandAssets.map((candidate) => (
              <button
                key={candidate.id}
                type="button"
                className={candidate.id === selectedAssetId ? "b2b-artwork-option is-selected" : "b2b-artwork-option"}
                onClick={() => setSelectedAssetId(candidate.id)}
              >
                <span className={`b2b-artwork-swatch bg-gradient-to-br ${candidate.previewStyle}`}>{candidate.name.slice(0, 1)}</span>
                <strong>{candidate.name}</strong>
                <small>{candidate.fileType} · {candidate.quality}</small>
              </button>
            ))}
          </div>
        ) : (
          <p>No artwork has been added yet.</p>
        )}
      </section>

      <section className="b2b-two-column">
        <article className="b2b-card">
          <span className="b2b-kicker">Order details</span>
          <h2>{product?.title ?? "Packaging"}</h2>
          <dl>
            <div><dt>Material</dt><dd>{variant?.material ?? "Pending"}</dd></div>
            <div><dt>Size</dt><dd>{variant?.size ?? "Pending"}</dd></div>
            <div><dt>Minimum</dt><dd>{variant?.moq ? `${variant.moq.toLocaleString()} units` : "Quote required"}</dd></div>
            <div><dt>Timing</dt><dd>{variant?.leadTime ?? "Quote required"}</dd></div>
          </dl>
        </article>

        {quote ? <QuoteBreakdownPanel quote={quote} /> : (
          <article className="b2b-card">
            <span className="b2b-kicker">Quote</span>
            <h2>Not sent yet</h2>
            <p>Request a quote when the proof looks right.</p>
          </article>
        )}
      </section>
    </PortalShell>
  );
}

function LiveMockup({
  productStyle,
  assetName,
  placement,
}: {
  productStyle?: string;
  assetName?: string;
  placement: PlacementSpec;
}) {
  return (
    <div className="b2b-mockup-stage">
      <div className={`b2b-packaging-object ${productStyle ?? "from-slate-100 via-white to-slate-200"}`}>
        <div className="b2b-packaging-seal" />
        <div className="b2b-packaging-label">PACKAGING PREVIEW</div>
        <div className="b2b-print-zone" style={{
          left: `${placement.x * 100 - placement.width * 60}%`,
          top: `${placement.y * 100 - placement.height * 60}%`,
          width: `${placement.width * 120}%`,
          height: `${placement.height * 120}%`,
        }} />
        <div className="b2b-placed-logo" style={{
          left: `${placement.x * 100}%`,
          top: `${placement.y * 100}%`,
          width: `${placement.width * 100}%`,
          height: `${placement.height * 100}%`,
          transform: `translate(-50%, -50%) rotate(${placement.rotationDegrees}deg)`,
        }}>
          <span>{assetName?.slice(0, 1) ?? "Q"}</span>
          <strong>{assetName ?? "Brand artwork"}</strong>
        </div>
      </div>
    </div>
  );
}

function PlacementControls({
  placement,
  setPlacement,
  safeAreaValid,
}: {
  placement: PlacementSpec;
  setPlacement: (placement: PlacementSpec) => void;
  safeAreaValid: boolean;
}) {
  const update = (key: keyof PlacementSpec, value: number) => {
    setPlacement({ ...placement, [key]: value });
  };

  return (
    <div className="b2b-placement-controls">
      <div className="b2b-card-row">
        <span className="b2b-kicker">Placement editor</span>
        <StatusBadge status={safeAreaValid ? "safe area valid" : "safe area review"} tone={safeAreaValid ? "good" : "warn"} />
      </div>
      <label>
        X position
        <input type="range" min="0.1" max="0.9" step="0.01" value={placement.x} onChange={(event) => update("x", Number(event.target.value))} />
      </label>
      <label>
        Y position
        <input type="range" min="0.1" max="0.9" step="0.01" value={placement.y} onChange={(event) => update("y", Number(event.target.value))} />
      </label>
      <label>
        Width
        <input type="range" min="0.12" max="0.62" step="0.01" value={placement.width} onChange={(event) => update("width", Number(event.target.value))} />
      </label>
      <label>
        Rotation
        <input type="range" min="-18" max="18" step="1" value={placement.rotationDegrees} onChange={(event) => update("rotationDegrees", Number(event.target.value))} />
      </label>
    </div>
  );
}
